import { getStyleColors } from '../../../../Helpers';
import type { SentimentTone } from '../GovernanceAnalyticsAiAnswers.utils';

/**
 * Single source of truth for the colours the Brand Presence analytics screens paint with.
 *
 * The brand colour is whatever the host theme sets as `--primary`; it is never hardcoded in a
 * component. The literals below are *fallbacks only*, used when `--primary` cannot be read at
 * all (jsdom in unit tests, or a host that ships no theme variables) — without them a chart
 * would render with no colour instead of a slightly-off one.
 */
const PRIMARY_COLOR_FALLBACK = '#009EE3';

/** A brand colour as the service stores and demands it: a lower-case six-digit hex. */
export const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/;

/** The theme's primary colour — the customer's own ("You") colour across every analytics screen. */
export function readPrimaryColor(): string {
  try {
    return getStyleColors().primary?.trim() || PRIMARY_COLOR_FALLBACK;
  } catch {
    return PRIMARY_COLOR_FALLBACK;
  }
}

/**
 * Categorical palette for the *other* series (competitors, breakdown slices). These are chart
 * colours, not brand colours: they only have to stay distinguishable from each other and from
 * the theme primary, so they stay explicit here rather than being derived from the theme.
 */
export const GOVERNANCE_COMPARISON_PALETTE = ['#FF5479', '#EE7402', '#A237E0', '#D96ECC', '#7491ED', '#2CAC34', '#3ABFF0'] as const;

/**
 * The chart palette for a screen that plots the customer's own series alongside others: the
 * theme's primary always leads, so "you" reads as the brand colour wherever it appears.
 */
export function governanceAnalyticsPalette(primaryColor: string): string[] {
  return [primaryColor, ...GOVERNANCE_COMPARISON_PALETTE];
}

/** How one grade of the verdict scale is painted, in each of the forms a surface draws it in. */
export interface VerdictSwatch {
  /** The full-strength colour: a shaded passage's accent bar, a key's swatch. */
  solid: string;
  /** The light tint a passage graded this way is shaded with. */
  tint: string;
  /** The text colour of the grade's thumb, as a utility class. */
  iconClassName: string;
  /** A card that reports this grade: its light ground and its hairline ring, as utility classes. */
  surfaceClassName: string;
}

/**
 * The colours of the five-grade verdict scale, shared by every surface that shows a grade: the
 * shaded passages of an answer and their key, the thumbs on the lists, cards and charts, and the
 * recommendation card. Changing a grade's colour here changes it everywhere.
 *
 * Keyed by band, most favourable first:
 * - `excellent` — recommends: a strong emerald.
 * - `good` — favourable: a lighter teal-green, one step below it.
 * - `neutral` — descriptive or balanced: a slate that recedes behind the graded passages.
 * - `negative` — raises a caveat: amber, kept well apart from the red of the grade below it.
 * - `veryNegative` — argues against: rose.
 *
 * Every surface draws a grade with its label or its thumb beside the colour, so no reading depends
 * on the colour alone.
 */
export const VERDICT_PALETTE: Readonly<Record<SentimentTone, VerdictSwatch>> = {
  excellent: {
    solid: '#059669',
    tint: '#10b98124',
    iconClassName: 'text-emerald-600',
    surfaceClassName: 'bg-emerald-50/80 ring-emerald-200',
  },
  good: {
    solid: '#2dd4bf',
    tint: '#2dd4bf1f',
    iconClassName: 'text-teal-600',
    surfaceClassName: 'bg-teal-50/80 ring-teal-200',
  },
  neutral: {
    solid: '#94a3b8',
    tint: '#94a3b81a',
    iconClassName: 'text-slate-400',
    surfaceClassName: 'bg-slate-50 ring-slate-200',
  },
  negative: {
    solid: '#f59e0b',
    tint: '#f59e0b1f',
    iconClassName: 'text-amber-600',
    surfaceClassName: 'bg-amber-50/80 ring-amber-200',
  },
  veryNegative: {
    solid: '#e11d48',
    tint: '#e11d481c',
    iconClassName: 'text-rose-600',
    surfaceClassName: 'bg-rose-50/80 ring-rose-200',
  },
};

/**
 * Same hue at a lower opacity — used for the "comparison" twin of a series so the pair stays
 * visually related. Accepts `#rgb` / `#rrggbb`; anything else (already-functional colours such
 * as a theme variable resolving to `rgb(...)`) is returned untouched rather than mangled.
 */
export function withAlpha(color: string, alpha: number): string {
  const value = color.trim();

  const hexMatch = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value);
  if (hexMatch) {
    const digits = hexMatch[1].length === 3 ? hexMatch[1].replace(/./g, (char) => char + char) : hexMatch[1];
    const parsed = Number.parseInt(digits, 16);
    return `rgba(${(parsed >> 16) & 255}, ${(parsed >> 8) & 255}, ${parsed & 255}, ${alpha})`;
  }

  const rgbMatch = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(value);
  if (rgbMatch) {
    return `rgba(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}, ${alpha})`;
  }

  return value;
}

interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** Parses `#rgb` / `#rrggbb` / `rgb()` / `rgba()` to channels, or undefined for anything else. */
function parseRgb(color: string): Rgb | undefined {
  const value = color.trim();

  const hexMatch = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value);
  if (hexMatch) {
    const digits = hexMatch[1].length === 3 ? hexMatch[1].replace(/./g, (char) => char + char) : hexMatch[1];
    const parsed = Number.parseInt(digits, 16);
    return { r: (parsed >> 16) & 255, g: (parsed >> 8) & 255, b: parsed & 255 };
  }

  const rgbMatch = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(value);
  if (rgbMatch) {
    return { r: Number(rgbMatch[1]), g: Number(rgbMatch[2]), b: Number(rgbMatch[3]) };
  }

  return undefined;
}

function toHex({ r, g, b }: Rgb): string {
  const channel = (value: number) =>
    Math.max(0, Math.min(255, Math.round(value)))
      .toString(16)
      .padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

/**
 * How far apart two colours look, on the redmean weighting (0 for identical, ~765 for black against
 * white). Undefined when either colour cannot be parsed, so an unreadable value never reads as
 * "close" and blocks a stored colour by accident.
 */
function colorDistance(a: string, b: string): number | undefined {
  const first = parseRgb(a);
  const second = parseRgb(b);
  if (!first || !second) {
    return undefined;
  }
  const meanR = (first.r + second.r) / 2;
  const dr = first.r - second.r;
  const dg = first.g - second.g;
  const db = first.b - second.b;
  return Math.sqrt((2 + meanR / 256) * dr * dr + 4 * dg * dg + (2 + (255 - meanR) / 256) * db * db);
}

/**
 * Below this redmean distance two colours are treated as the same on screen, so a stored colour
 * this close to one already assigned — or to the customer's own colour — falls back to the palette
 * rather than being painted twice.
 */
export const PERCEPTUAL_DISTANCE_THRESHOLD = 100;

/** Whether two colours are close enough to be confused, and so must not both appear on one screen. */
export function colorPerceptuallyClose(a: string, b: string): boolean {
  const distance = colorDistance(a, b);
  return distance !== undefined && distance < PERCEPTUAL_DISTANCE_THRESHOLD;
}

function relativeLuminance({ r, g, b }: Rgb): number {
  const channel = (value: number) => {
    const scaled = value / 255;
    return scaled <= 0.03928 ? scaled / 12.92 : ((scaled + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function contrastRatio(a: Rgb, b: Rgb): number {
  const first = relativeLuminance(a);
  const second = relativeLuminance(b);
  const lighter = Math.max(first, second);
  const darker = Math.min(first, second);
  return (lighter + 0.05) / (darker + 0.05);
}

/** The light ground the analytics names sit on when a surface is not named. */
export const READABLE_TEXT_SURFACE = '#ffffff';

/** The contrast ratio a coloured name must clear against its surface to stay legible (WCAG AA). */
export const READABLE_TEXT_MIN_CONTRAST = 4.5;

/**
 * A variant of `color` dark enough to read against `surface`.
 *
 * Fills use the stored colour as it is; text uses this, because a mid-tone brand colour that reads
 * well as a block is often too light to read as a name on a near-white ground. The hue is kept —
 * every channel is scaled toward black by the same factor — and a colour that already clears the
 * ratio is returned untouched. An unparseable colour is returned as given rather than guessed at.
 */
export function readableTextColor(color: string, surface: string = READABLE_TEXT_SURFACE): string {
  const base = parseRgb(color);
  const ground = parseRgb(surface) ?? { r: 255, g: 255, b: 255 };
  if (!base) {
    return color;
  }
  if (contrastRatio(base, ground) >= READABLE_TEXT_MIN_CONTRAST) {
    return color;
  }

  // The contrast is checked on the ROUNDED channels the hex will actually carry, not the darker
  // floats before rounding — otherwise the returned colour can round back up and miss the ratio.
  const roundChannels = ({ r, g, b }: Rgb): Rgb => ({ r: Math.round(r), g: Math.round(g), b: Math.round(b) });
  let candidate = roundChannels(base);
  for (let step = 0; step < 24 && contrastRatio(candidate, ground) < READABLE_TEXT_MIN_CONTRAST; step += 1) {
    const scale = 0.9 ** (step + 1);
    candidate = roundChannels({ r: base.r * scale, g: base.g * scale, b: base.b * scale });
  }
  return toHex(candidate);
}
